import React, { useState, useEffect } from "react";
import { getYear } from "date-fns/fp";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import useMediaQuery, {
  LARGER_THAN_LAPTOP,
} from "storefront/hooks/useMediaQuery";
import LinkButton from "storefront/components/LinkButton";
import trustMicrositeViewed from "storefront/Analytics/EventCreators/trustMicrositeViewed";
import useAnalytics from "storefront/hooks/useAnalytics";
import InstagramLogo from "storefront/components/Icons/InstagramLogo";
import FacebookLogo from "storefront/components/Icons/FacebookLogo";
import YoutubeLogo from "storefront/components/Icons/YoutubeLogo";
import LinkedinLogo from "storefront/components/Icons/LinkedinLogo";
import MobileFooter from "./MobileFooter/index";
import SiteLink from "./SiteLink";
import SocialLink from "./SocialLink";

// eslint-disable-next-line css-modules/no-unused-class
import siteLinkStyles from "./SiteLink.module.scss";
import styles from "./Footer.module.scss";

const Footer = () => {
  const [hideTopBorder, setHideTopBorder] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const { t } = useTranslation(["common", "helpCenterUrls"]);
  const { track } = useAnalytics();
  const isLargerThanLaptop = useMediaQuery(LARGER_THAN_LAPTOP);

  useEffect(() => {
    // NOTE(McRae): these pages have a non-white background at the end so we do not want a top border
    const isAboutPage = window.location.pathname === "/about";
    const isArticleShowPage = window.location.pathname.includes(
      `${t("common::BLOG_LINK_PATH")}/`,
    );

    if (isAboutPage || isArticleShowPage) {
      setHideTopBorder(true);
    }
  }, [t]);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <footer
      className={classNames(styles.pageFooter, {
        [styles.isBorderless]: hideTopBorder,
      })}
    >
      {!isLargerThanLaptop ? <MobileFooter /> : null}
      <div className={styles.links}>
        <SiteLink className={styles.siteLink} href="/about" rel="nofollow">
          About
        </SiteLink>
        {mounted ? (
          <LinkButton
            // noop -- the modal opens magically
            onClick={() => {}}
            variant="inherit"
            className={classNames(
              "ot-sdk-show-settings", // this className handles the modal opening & closing
              styles.siteLink,
              siteLinkStyles.siteLink,
            )}
          >
            Your Privacy Choices
          </LinkButton>
        ) : null}
        <SiteLink
          className={styles.siteLink}
          href={t("helpCenterUrls::helpCenterHome")}
          rel="nofollow"
        >
          Help &amp; FAQ
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="/about/terms"
          rel="nofollow"
        >
          Terms
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="/about/privacy"
          rel="nofollow"
        >
          Privacy
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="/trust"
          onClick={() => track(trustMicrositeViewed("footer"))}
          rel="nofollow"
        >
          Trust
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="/about/accessibility"
          rel="nofollow"
        >
          Accessibility
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="/about/contact"
          rel="nofollow"
        >
          Contact
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href="https://job-boards.greenhouse.io/goatgroup"
          rel="nofollow"
        >
          Jobs
        </SiteLink>
        <SiteLink
          className={styles.siteLink}
          href={`https://itunes.apple.com/app/id${t("common::IOS_APP_ID")}`}
          rel="noopener noreferrer nofollow"
        >
          iOS App
        </SiteLink>

        <SocialLink
          className={styles.socialLink}
          href={`https://www.instagram.com/${t("common::INSTAGRAM_USERNAME")}`}
        >
          <InstagramLogo />
        </SocialLink>
        <SocialLink
          className={styles.socialLink}
          href={`https://www.facebook.com/${t("common::FACEBOOK_USERNAME")}`}
        >
          <FacebookLogo />
        </SocialLink>
        <SocialLink
          className={styles.socialLink}
          href={`https://www.youtube.com/channel/${t(
            "common::YOUTUBE_USERNAME",
          )}`}
        >
          <YoutubeLogo />
        </SocialLink>
        <SocialLink
          className={styles.socialLink}
          href={`https://www.linkedin.com/company/${t(
            "common::LINKEDIN_USERNAME",
          )}`}
        >
          <LinkedinLogo />
        </SocialLink>

        <span className={styles.copyright}>
          {t("common::MARKETPLACE_ID")} &copy; {getYear(new Date())}
        </span>
      </div>
    </footer>
  );
};

export default Footer;
